import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Send, CheckCircle2 } from "lucide-react";
import { SITE } from "../../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  full_name: "",
  email: "",
  phone: "",
  event_type: "Mariage",
  event_date: "",
  location: "",
  guests: "",
  budget: "",
  message: "",
};

const EVENT_TYPES = [
  "Mariage",
  "Anniversaire",
  "Soirée privée",
  "Soirée d'entreprise",
  "Événement public",
  "Club / Discothèque",
  "Festival",
  "Autre",
];

const BUDGETS = [
  "Entre 1 500 € et 2 000 €",
  "Plus de 2 000 €",
];

export const ContactForm = () => {
  const [data, setData] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!data.full_name || !data.email || !data.phone || !data.event_type) {
      toast.error("Merci de remplir les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, data);
      setDone(true);
      toast.success("Demande envoyée ! Je vous réponds très vite.");
      setData(initial);
    } catch (err) {
      console.error(err);
      toast.error(
        "Une erreur est survenue. Vous pouvez aussi me contacter par WhatsApp."
      );
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/60 focus:border-[#D4AF37]/40 transition";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-black border-t border-white/5"
    >
      <Toaster
        theme="dark"
        position="top-center"
        toastOptions={{
          style: {
            background: "#0A0A0A",
            border: "1px solid rgba(212,175,55,0.4)",
            color: "#fff",
          },
        }}
      />
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <span className="eyebrow">Contact</span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Demandez votre <span className="gold-text-gradient">devis personnalisé</span>
          </h2>
          <p className="mt-4 text-zinc-400 max-w-2xl mx-auto">
            Réponse rapide, devis clair, conseils inclus. Aucune obligation.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="glass rounded-3xl p-6 md:p-10"
        >
          {done ? (
            <div
              data-testid="contact-success"
              className="flex flex-col items-center text-center py-10"
            >
              <CheckCircle2 size={56} className="text-[#D4AF37]" />
              <h3 className="mt-5 text-2xl font-bold text-white">
                Demande bien reçue !
              </h3>
              <p className="mt-3 text-zinc-400 max-w-md">
                Merci de votre confiance. Je reviens vers vous très vite par
                email ou téléphone avec une proposition adaptée à votre projet.
              </p>
              <button
                onClick={() => setDone(false)}
                data-testid="contact-new-request-btn"
                className="mt-7 btn-outline-gold rounded-full px-6 py-3 text-sm"
              >
                Faire une nouvelle demande
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2 block">
                  Nom & prénom *
                </label>
                <input
                  type="text"
                  required
                  value={data.full_name}
                  onChange={update("full_name")}
                  data-testid="contact-name-input"
                  placeholder="Marie Dupont"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2 block">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={data.email}
                  onChange={update("email")}
                  data-testid="contact-email-input"
                  placeholder="vous@exemple.com"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2 block">
                  Téléphone *
                </label>
                <input
                  type="tel"
                  required
                  value={data.phone}
                  onChange={update("phone")}
                  data-testid="contact-phone-input"
                  placeholder="06 12 34 56 78"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2 block">
                  Type d'événement *
                </label>
                <select
                  value={data.event_type}
                  onChange={update("event_type")}
                  data-testid="contact-event-type-select"
                  className={inputCls}
                >
                  {EVENT_TYPES.map((t) => (
                    <option key={t} value={t} className="bg-[#0A0A0A]">
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2 block">
                  Date de l'événement
                </label>
                <input
                  type="date"
                  value={data.event_date}
                  onChange={update("event_date")}
                  data-testid="contact-date-input"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2 block">
                  Lieu
                </label>
                <input
                  type="text"
                  value={data.location}
                  onChange={update("location")}
                  data-testid="contact-location-input"
                  placeholder="Ville, salle, domaine..."
                  className={inputCls}
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2 block">
                  Nombre d'invités
                </label>
                <input
                  type="text"
                  value={data.guests}
                  onChange={update("guests")}
                  data-testid="contact-guests-input"
                  placeholder="Ex : 80 - 120 invités"
                  className={inputCls}
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2 block">
                  Budget envisagé
                </label>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                  {BUDGETS.map((b) => (
                    <button
                      type="button"
                      key={b}
                      onClick={() => setData((d) => ({ ...d, budget: b }))}
                      data-testid={`contact-budget-${b.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                      className={`px-3 py-3 rounded-xl text-xs font-medium border transition-all text-left ${
                        data.budget === b
                          ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#E9C766]"
                          : "border-white/10 bg-white/[0.02] text-zinc-300 hover:border-white/25"
                      }`}
                    >
                      {b}
                    </button>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2 block">
                  Message
                </label>
                <textarea
                  rows={5}
                  value={data.message}
                  onChange={update("message")}
                  data-testid="contact-message-input"
                  placeholder="Parlez-moi de l'ambiance souhaitée, du déroulé, de vos attentes..."
                  className={inputCls}
                />
              </div>
              <div className="md:col-span-2 flex flex-col sm:flex-row gap-4 items-center justify-between mt-2">
                <p className="text-xs text-zinc-500">
                  Vos données sont uniquement utilisées pour vous répondre. Elles
                  ne seront jamais transmises à des tiers.
                </p>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-btn"
                  className="btn-gold rounded-full px-8 py-4 text-sm md:text-base inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? "Envoi..." : "Envoyer ma demande"}
                  <Send size={16} />
                </button>
              </div>
            </form>
          )}
        </motion.div>

        <p className="text-center text-zinc-500 text-sm mt-8">
          Préférez-vous échanger directement ?{" "}
          <a
            href={`mailto:${SITE.email}`}
            className="text-[#D4AF37] hover:underline"
          >
            {SITE.email}
          </a>{" "}
          ·{" "}
          <a
            href={`tel:${SITE.phoneInternational}`}
            className="text-[#D4AF37] hover:underline"
          >
            {SITE.phoneDisplay}
          </a>
        </p>
      </div>
    </section>
  );
};
